import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { IconButton } from '@mui/material';
import i18n from 'i18next';
import { useNavigate } from 'react-router-dom';

import { Icon, Button, Avatar, Tooltip } from 'modules/Core/Common';
import {
  WidgetCard,
  WidgetCardHeader,
  WidgetCardSubHeader,
  WidgetCardContent,
  WidgetEmptyData,
  IntervalOptionsMenu,
} from 'modules/Dashboard/Widgets/Common';
import { useMediaQuery, MOBILE_SIZE, TABLET_SIZE } from 'modules/Core/Hooks';
import { AnalyticsIcon } from '@edusynch/edusynch-svg-icons';
import { format } from 'date-fns';
import emptyDataImg from 'assets/layout/empty-data/widgets-empty-data.svg?url';

import { ChartToggle, ProgressLineChart, SkillsBarChart } from './Charts';
import * as S from './StudentsProgress.styles';

const I18N_PREFIX = 'modules.widgets.students_progress';

export const StudentsProgress = ({
  widget,
  intervalOption,
  startDate,
  endDate,
  selectedTestId,
  selectedTestName,
  onChangeIntervalOption,
  data,
  onOpenSettings,
  onOpenFilter,
  hasFiltersActive,
  isMock,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(MOBILE_SIZE);
  const isTablet = useMediaQuery(TABLET_SIZE);
  const [activeView, setActiveView] = useState('line');

  const widgetSize = isMobile || isTablet ? 'small' : widget?.settings?.size;
  const hasStudent = !!data?.student;
  const canViewProfile = !isMock && !!data?.student?.id;
  const hasPerformanceData = hasStudent && !!data?.performance;
  const hasChartData =
    hasPerformanceData &&
    (data?.performance?.chart_history?.length > 0 ||
      data?.performance?.chart_skills?.length > 0);

  const hasDateRange = startDate && endDate;

  const formatDateDisplay = (date) => {
    try {
      return format(new Date(date), 'dd/MM/yyyy');
    } catch {
      return '';
    }
  };

  const renderPeriodText = () => {
    if (hasDateRange) {
      return `${formatDateDisplay(startDate)} - ${formatDateDisplay(endDate)}`;
    }
    return `${i18n.t('common-words.last')} ${intervalOption} ${
      intervalOption === '12'
        ? i18n.t('common-words.months')
        : i18n.t('common-words.days')
    }`;
  };

  const toISODate = (date) => {
    try {
      return new Date(date).toISOString();
    } catch {
      return null;
    }
  };

  const handleProfileView = () => {
    // Carrega o mesmo intervalo de data selecionado no filtro do dashboard
    // para o Profile view, para que a tela de perfil abra com o mesmo período.
    const params = new URLSearchParams();

    if (hasDateRange) {
      const start = toISODate(startDate);
      const end = toISODate(endDate);
      if (start && end) {
        params.set('start_date', start);
        params.set('end_date', end);
      }
    } else if (intervalOption) {
      params.set('days', String(intervalOption));
    }

    // Carrega tambem o teste (exam) selecionado no filtro do dashboard, para
    // que o Profile view abra no mesmo teste em vez do primeiro da lista.
    // Enviamos id e nome: o profile casa por id e, se os ids nao baterem
    // entre os endpoints, cai no nome como fallback.
    if (selectedTestId) {
      params.set('test_id', String(selectedTestId));
    }
    if (selectedTestName) {
      params.set('test_name', selectedTestName);
    }

    const query = params.toString();
    navigate(`/students/profile/${data.student.id}${query ? `?${query}` : ''}`);
  };

  const renderStudentHeader = () => (
    <S.StudentHeaderRow>
      <S.StudentHeader>
        <Avatar
          src={data.student?.avatar_url}
          name={data.student?.name}
          size="large"
        />
        <S.StudentInfo>
          <span>{data.student?.name}</span>
          {selectedTestName && <S.TestName>{selectedTestName}</S.TestName>}
        </S.StudentInfo>
      </S.StudentHeader>
      <S.StudentActions>
        {hasDateRange && <h6>{renderPeriodText()}</h6>}
        <IntervalOptionsMenu
          defaultOption={hasDateRange ? null : intervalOption}
          onChangeOption={onChangeIntervalOption}
        />
        {canViewProfile && (
          <Tooltip
            overlay={i18n.t('commons.buttons.actions-buttons.profile-view')}
          >
            <IconButton
              aria-label="profile-view"
              id="profile-view"
              data-cy="profile-view-btn"
              onClick={handleProfileView}
            >
              <Icon name="user" />
            </IconButton>
          </Tooltip>
        )}
        {isMock && widgetSize === 'large' && (
          <IconButton
            aria-label="settings"
            id="settings"
            onClick={onOpenSettings}
          >
            <Icon name="settings" />
          </IconButton>
        )}
        {widgetSize === 'large' && (
          <IconButton
            aria-label="users-filter"
            id="users-filter"
            onClick={onOpenFilter}
            sx={
              hasFiltersActive
                ? {
                    position: 'relative',
                    '::before': {
                      content: '""',
                      position: 'absolute',
                      width: '0.8rem',
                      height: '0.8rem',
                      borderRadius: '50%',
                      top: '0.8rem',
                      right: '0.8rem',
                      bgcolor: theme.config.colors.green,
                    },
                  }
                : undefined
            }
          >
            <Icon name="filter" />
          </IconButton>
        )}
      </S.StudentActions>
    </S.StudentHeaderRow>
  );

  const renderEmptyState = () => {
    if (!hasStudent) {
      return (
        <S.EmptyStateContainer>
          <WidgetEmptyData
            imageURL={emptyDataImg}
            title={i18n.t(`${I18N_PREFIX}.no_filter_applied`)}
            subtitle={i18n.t(`${I18N_PREFIX}.add_filter_subtitle`)}
          />
          <S.EmptyStateButton>
            <Button medium onClick={onOpenFilter}>
              {i18n.t(`${I18N_PREFIX}.add_filter_button`)}
            </Button>
          </S.EmptyStateButton>
        </S.EmptyStateContainer>
      );
    }

    return (
      <S.EmptyStateContainer>
        <WidgetEmptyData
          imageURL={emptyDataImg}
          title={i18n.t(`${I18N_PREFIX}.no_data_available`)}
          subtitle={i18n.t(`${I18N_PREFIX}.change_filter_subtitle`)}
        />
        <S.EmptyStateButton>
          <Button medium onClick={onOpenFilter}>
            {i18n.t(`${I18N_PREFIX}.change_filter_button`)}
          </Button>
        </S.EmptyStateButton>
      </S.EmptyStateContainer>
    );
  };

  const renderChartContent = () => {
    if (!hasChartData) {
      return renderEmptyState();
    }

    return (
      <S.ChartContainer>
        <S.ChartToggleWrapper>
          <ChartToggle activeView={activeView} onToggle={setActiveView} />
        </S.ChartToggleWrapper>
        <S.ChartArea>
          {activeView === 'line' ? (
            <ProgressLineChart
              data={data?.performance?.chart_history}
              marks={data?.performance?.marks}
              size={widgetSize}
            />
          ) : (
            <SkillsBarChart
              data={data?.performance?.chart_skills}
              size={widgetSize}
            />
          )}
        </S.ChartArea>
      </S.ChartContainer>
    );
  };

  return (
    <WidgetCard size={widgetSize}>
      {hasStudent ? (
        renderStudentHeader()
      ) : (
        <WidgetCardHeader
          size={widgetSize}
          Icon={AnalyticsIcon}
          title={i18n.t(`${I18N_PREFIX}.title`)}
          subtitle={i18n.t(`${I18N_PREFIX}.subtitle`)}
          showSettingsButton={isMock && widgetSize === 'small'}
          showFiltersButton
          onOpenSettings={onOpenSettings}
          onOpenFilters={onOpenFilter}
          hasFiltersActive={hasFiltersActive}
          borderBottom={widgetSize === 'small'}
        >
          <WidgetCardSubHeader size={widgetSize}>
            <S.ContentAction>
              {isMock && widgetSize === 'large' && (
                <IconButton
                  aria-label="settings"
                  id="settings"
                  onClick={onOpenSettings}
                >
                  <Icon name="settings" />
                </IconButton>
              )}
              <IntervalOptionsMenu
                defaultOption={intervalOption}
                onChangeOption={onChangeIntervalOption}
              />
              {widgetSize === 'large' && (
                <IconButton
                  aria-label="users-filter"
                  id="users-filter"
                  onClick={onOpenFilter}
                  sx={
                    hasFiltersActive
                      ? {
                          position: 'relative',
                          '::before': {
                            content: '""',
                            position: 'absolute',
                            width: '0.8rem',
                            height: '0.8rem',
                            borderRadius: '50%',
                            top: '0.8rem',
                            right: '0.8rem',
                            bgcolor: theme.config.colors.green,
                          },
                        }
                      : undefined
                  }
                >
                  <Icon name="filter" />
                </IconButton>
              )}
            </S.ContentAction>
          </WidgetCardSubHeader>
        </WidgetCardHeader>
      )}
      <WidgetCardContent size={widgetSize} borderTop>
        {hasStudent ? renderChartContent() : renderEmptyState()}
      </WidgetCardContent>
    </WidgetCard>
  );
};

StudentsProgress.propTypes = {
  widget: PropTypes.object,
  data: PropTypes.object,
  intervalOption: PropTypes.string,
  startDate: PropTypes.any,
  endDate: PropTypes.any,
  selectedTestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedTestName: PropTypes.string,
  onChangeIntervalOption: PropTypes.func,
  onOpenSettings: PropTypes.func,
  onOpenFilter: PropTypes.func,
  hasFiltersActive: PropTypes.bool,
  isMock: PropTypes.bool,
};
